import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ClipboardList, Filter, RefreshCw, X as XIcon, User, Calendar,
  Briefcase, Clock, CheckCircle, AlertTriangle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import './ControlAsignaciones.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORES_ASESOR = ['#1565C0', '#E53935', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#D81B60', '#5E35B1', '#F4511E', '#00897B', '#6D4C41', '#546E7A'];

const ControlAsignaciones = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filtros, setFiltros] = useState({ asesor: '', tipo_gestion: '', estado: '', fecha_desde: '', fecha_hasta: '' });
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('mes');

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const qp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qp.append(k, v); });
      const url = `${API}/control-asignaciones/datos${qp.toString() ? '?' + qp : ''}`;
      const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(resp.data);
    } catch (err) {
      toast.error('Error al cargar datos de asignaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const aplicarFiltros = () => { setFiltrosAplicados(true); fetchData(filtros); };
  const limpiarFiltros = () => {
    const limpio = { asesor: '', tipo_gestion: '', estado: '', fecha_desde: '', fecha_hasta: '' };
    setFiltros(limpio);
    setFiltrosAplicados(false);
    fetchData();
  };

  const resumen = data?.resumen_global || {};
  const asesores = data?.asesores || [];
  const filtrosDisp = data?.filtros_disponibles || {};
  const resumenPeriodo = data?.resumen_periodo || {};

  // Group timeline by date
  const timelineGrouped = useMemo(() => {
    if (!data?.timeline) return [];
    const groups = {};
    for (const entry of data.timeline) {
      const fecha = (entry.fecha || '').substring(0, 10) || 'Sin fecha';
      if (!groups[fecha]) groups[fecha] = { fecha, items: [] };
      groups[fecha].items.push(entry);
    }
    return Object.values(groups).sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [data?.timeline]);

  // Period data for bars
  const periodData = resumenPeriodo[periodoActivo] || [];
  const maxPeriod = Math.max(...periodData.map(p => p.casos), 1);

  const totalPeriodo = useMemo(() => {
    return periodData.reduce((sum, p) => sum + p.casos, 0);
  }, [periodData]);

  const getEstadoClass = (estado) => {
    if (!estado) return '';
    if (estado.includes('Registrado')) return 'Registrado';
    if (estado.includes('Derivado')) return 'Derivado';
    if (estado.includes('Completada') || estado.includes('Archivada')) return 'Completada';
    return 'EnProceso';
  };

  if (loading && !data) {
    return (
      <div className="ctrl-asig-container">
        <div className="ctrl-loading" data-testid="ctrl-loading">
          <div className="spinner"></div>
          <p>Cargando control de asignaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ctrl-asig-container" data-testid="control-asignaciones">
      <div className="ctrl-asig-header">
        <h1><ClipboardList size={26} /> Control de Asignaciones CNC</h1>
        <p>Historial y carga de trabajo de asesores, organizado cronologicamente</p>
      </div>

      {/* Filters */}
      <div className="ctrl-asig-filtros" data-testid="ctrl-filtros">
        <div className="filtro-g">
          <label>Asesor CNC</label>
          <select data-testid="filtro-asesor" value={filtros.asesor} onChange={e => setFiltros(p => ({ ...p, asesor: e.target.value }))}>
            <option value="">Todos</option>
            {(filtrosDisp.asesores || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filtro-g">
          <label>Tipo de caso</label>
          <select data-testid="filtro-tipo" value={filtros.tipo_gestion} onChange={e => setFiltros(p => ({ ...p, tipo_gestion: e.target.value }))}>
            <option value="">Todos</option>
            {(filtrosDisp.tipos || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="filtro-g">
          <label>Estado</label>
          <select data-testid="filtro-estado" value={filtros.estado} onChange={e => setFiltros(p => ({ ...p, estado: e.target.value }))}>
            <option value="">Todos</option>
            {(filtrosDisp.estados || []).map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="filtro-g">
          <label>Desde</label>
          <input data-testid="filtro-desde" type="date" value={filtros.fecha_desde} onChange={e => setFiltros(p => ({ ...p, fecha_desde: e.target.value }))} />
        </div>
        <div className="filtro-g">
          <label>Hasta</label>
          <input data-testid="filtro-hasta" type="date" value={filtros.fecha_hasta} onChange={e => setFiltros(p => ({ ...p, fecha_hasta: e.target.value }))} />
        </div>
        <div className="filtro-acciones">
          <button className="btn-app" onClick={aplicarFiltros} data-testid="btn-filtrar"><Filter size={13} /> Filtrar</button>
          {filtrosAplicados && <button className="btn-lmp" onClick={limpiarFiltros} data-testid="btn-limpiar"><XIcon size={13} /></button>}
          <button className="btn-lmp" onClick={() => fetchData(filtrosAplicados ? filtros : {})} data-testid="btn-refresh"><RefreshCw size={13} /></button>
        </div>
      </div>

      {/* KPIs */}
      <div className="ctrl-asig-kpis" data-testid="ctrl-kpis">
        <div className="ctrl-kpi">
          <div className="ctrl-kpi-icon" style={{ background: '#1565C0' }}><Briefcase size={20} /></div>
          <div><h3 data-testid="kpi-total">{resumen.total_gestiones || 0}</h3><span>Total Casos</span></div>
        </div>
        <div className="ctrl-kpi">
          <div className="ctrl-kpi-icon" style={{ background: '#43A047' }}><User size={20} /></div>
          <div><h3 data-testid="kpi-asesores">{resumen.total_asesores || 0}</h3><span>Asesores</span></div>
        </div>
        <div className="ctrl-kpi">
          <div className="ctrl-kpi-icon" style={{ background: '#FB8C00' }}><BarChart3 size={20} /></div>
          <div><h3 data-testid="kpi-tipos">{resumen.tipos_gestion || 0}</h3><span>Tipos</span></div>
        </div>
        <div className="ctrl-kpi">
          <div className="ctrl-kpi-icon" style={{ background: '#8E24AA' }}><Calendar size={20} /></div>
          <div><h3 data-testid="kpi-periodo">{totalPeriodo}</h3><span>Casos ({periodoActivo === 'dia' ? 'Hoy' : periodoActivo === 'semana' ? 'Semana' : 'Mes'})</span></div>
        </div>
      </div>

      {/* Period Summary */}
      <div className="ctrl-periodo-tabs" data-testid="periodo-tabs">
        {[
          { key: 'dia', label: 'Hoy' },
          { key: 'semana', label: 'Ultima semana' },
          { key: 'mes', label: 'Ultimo mes' }
        ].map(p => (
          <button key={p.key} className={`ctrl-periodo-tab ${periodoActivo === p.key ? 'active' : ''}`} onClick={() => setPeriodoActivo(p.key)} data-testid={`tab-${p.key}`}>
            <h4>{p.label}</h4>
            <div className="periodo-num">{(resumenPeriodo[p.key] || []).reduce((s, x) => s + x.casos, 0)}</div>
            <span style={{ fontSize: '0.7rem' }}>asignaciones</span>
          </button>
        ))}
      </div>

      {periodData.length > 0 && (
        <div className="ctrl-periodo-detalle" data-testid="periodo-detalle">
          <h3>Distribucion por asesor - {periodoActivo === 'dia' ? 'Hoy' : periodoActivo === 'semana' ? 'Ultima semana' : 'Ultimo mes'}</h3>
          <div className="ctrl-periodo-bars">
            {periodData.map((p, i) => (
              <div className="ctrl-bar-row" key={p.asesor}>
                <div className="ctrl-bar-name">{p.asesor}</div>
                <div className="ctrl-bar-track">
                  <div className="ctrl-bar-fill" style={{ width: `${(p.casos / maxPeriod) * 100}%`, background: COLORES_ASESOR[i % COLORES_ASESOR.length] }}>
                    {p.casos > 0 && <span>{p.casos}</span>}
                  </div>
                </div>
                <div className="ctrl-bar-count">{p.casos}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Asesor Cards */}
      <div className="ctrl-asesor-grid" data-testid="asesor-grid">
        {asesores.map((a, i) => (
          <div className="ctrl-asesor-card" key={a.nombre}>
            <div className="ctrl-asesor-head">
              <h3><User size={14} style={{ color: COLORES_ASESOR[i % COLORES_ASESOR.length] }} /> {a.nombre}</h3>
              <span className="ctrl-asesor-total">{a.total}</span>
            </div>
            <div className="ctrl-asesor-stats">
              <div className="ctrl-asesor-stat"><span className="num" style={{ color: '#1565C0' }}>{a.registrado}</span><span className="lbl">Registrado</span></div>
              <div className="ctrl-asesor-stat"><span className="num" style={{ color: '#7B1FA2' }}>{a.en_proceso}</span><span className="lbl">En Proceso</span></div>
              <div className="ctrl-asesor-stat"><span className="num" style={{ color: '#E65100' }}>{a.derivado}</span><span className="lbl">Derivado</span></div>
              <div className="ctrl-asesor-stat"><span className="num" style={{ color: '#2E7D32' }}>{a.completado}</span><span className="lbl">Completado</span></div>
            </div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="ctrl-timeline-section" data-testid="timeline-section">
        <h2><Clock size={16} /> Linea de Tiempo de Asignaciones</h2>
        {timelineGrouped.length > 0 ? (
          <div className="ctrl-timeline">
            {timelineGrouped.map(group => (
              <div className="ctrl-tl-group" key={group.fecha}>
                <div className="ctrl-tl-date">
                  <span>{group.fecha}</span>
                  <span className="count-badge">{group.items.length} caso{group.items.length > 1 ? 's' : ''}</span>
                </div>
                <div className="ctrl-tl-items">
                  {group.items.map((item, idx) => (
                    <div className={`ctrl-tl-item ${item.es_urgente ? 'urgente' : ''}`} key={`${item.id}-${idx}`}>
                      <span className="ctrl-tl-nom">{item.nomenclatura}</span>
                      <span className="ctrl-tl-tipo">{item.tipo_gestion}</span>
                      <span className="ctrl-tl-asesor">{item.asesor}</span>
                      {item.institucion && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{item.institucion}</span>}
                      <span className={`ctrl-tl-estado ${getEstadoClass(item.estado)}`}>{item.estado}</span>
                      {item.es_urgente && <AlertTriangle size={13} color="#F44336" />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ctrl-empty">
            <ClipboardList size={40} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
            <p>Sin asignaciones para los filtros seleccionados</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ControlAsignaciones;
